import { DatePipe, NgClass } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormArray, FormControl, ReactiveFormsModule } from '@angular/forms';
import { AuthService } from '@app/auth/auth.service';
import { EventModel } from '@app/events/shared/event.model';
import { EventRulesConductService } from '@app/events/shared/event-rules-conduct.service';
import { ButtonComponent } from '@app/partials/form/button/button.component';
import { ConfirmationModalService } from '@app/partials/modal/confirmation-modal.service';
import { ToastService } from '@app/shared/toast/toast.service';
import { UserModel } from '@app/users/shared/user.model';
import { CircleCheck, CircleDashed, LucideAngularModule, RotateCcw } from 'lucide-angular';

export interface RulesConductChecklistItem {
  label: string;
  body: string;
}

export const RULES_CONDUCT_CHECKLIST: ReadonlyArray<RulesConductChecklistItem> = [
  {
    label: 'FREQUENTLY ASKED QUESTIONS (FAQs)',
    body: 'We have read and understood the venues FAQs.',
  },
  {
    label: 'ALCOHOL',
    body: 'No alcohol may be brought on site including drinks brought in by guests for personal consumption. The venue licensees reserve the right to confiscate any unauthorised alcohol and ask the offending guests to leave the property. If alcohol is given as a gift, please ensure that you make your personal wedding planner aware so that you can put it in safekeeping to prevent any fines being incurred.',
  },
  {
    label: 'DECORATIONS',
    body: 'Please do not damage the premises in any way or use nails, screws, staples, glue, tape or any other permanently damaging fixatives to decorate and adorn the venue. The venue team will be on hand on the morning of your wedding, therefore, if anyone is unsure about something, please ask, as you may be liable for any damage or unnecessary cleaning caused.',
  },
  {
    label: 'DELIVERY, COLLECTION AND STORAGE',
    body: 'If you would like to deliver decorations or other items the day before your hire date, please arrange this with your personal wedding planner. Access to the venue will be from 9am on the morning of your wedding. Please refer to our FAQs for further detail. Any items of lost property will be kept for no longer than 7 days.',
  },
  {
    label: 'CONFETTI',
    body: 'You and your guests are welcome to bring confetti as long as it is dried natural flower petals or lavender only and must only be used outside. Paper confetti is not allowed even if it is recyclable. Please be aware there will be a minimum £100 cleaning charge for doing so.',
  },
  {
    label: 'TABLE GEMS',
    body: 'We do not allow table gems within the venue.',
  },
  {
    label: 'ALCOHOLIC FAVOURS',
    body: 'Please be aware that no alcoholic favours can be brought into the venue.',
  },
  {
    label: 'GIFTS, CARDS AND VALUABLES',
    body: "We will not be responsible for any gifts, cards or other valuables including cash brought into the venue by you or your guests. Any such items are left at the owner's risk.",
  },
  {
    label: 'PACKAGING AND WASTE',
    body: 'Please note that you are responsible for taking away all packaging and decorations after your wedding. This includes waste from your suppliers. Our bins are reserved for our own use. Any items left behind or put in our bins will result in a waste removal charge.',
  },
  {
    label: 'LEFTOVER FOOD',
    body: 'No leftover food which was provided by us can be taken off site at the end of your wedding. The only exception is a cheese wedding cake.',
  },
  {
    label: 'VIEWINGS ON WEDDING DAYS',
    body: 'Viewings of the venue for potential new couples may occur on the morning of your wedding.',
  },
];

@Component({
  selector: 'app-event-rules-conduct',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    NgClass,
    DatePipe,
    LucideAngularModule,
    ButtonComponent,
  ],
  templateUrl: './rules-conduct.component.html',
  styleUrls: [],
})
export class RulesConductComponent implements OnInit {
  @Input({ required: true }) public event!: EventModel;

  /**
   * TVG-188: when true the acknowledgement checklist is read-only and the
   * submit/admin footer is suppressed, letting the customer portal render the
   * agreement as readable-but-not-yet-submittable before the six week window
   * opens. Defaults to false so the staff view is unchanged.
   */
  @Input() public submissionLocked = false;

  @Output() public eventUpdated = new EventEmitter<EventModel>();

  protected readonly checklist = RULES_CONDUCT_CHECKLIST;
  protected readonly CircleCheckIcon = CircleCheck;
  protected readonly CircleDashedIcon = CircleDashed;
  protected readonly ResetIcon = RotateCcw;

  protected acknowledgements = new FormArray<FormControl<boolean>>(
    RULES_CONDUCT_CHECKLIST.map(() => new FormControl<boolean>(false, { nonNullable: true })),
  );

  protected submitting = false;
  protected resetting = false;
  protected isAdmin = false;
  protected currentUser: UserModel | null = null;

  constructor(
    private rulesConductService: EventRulesConductService,
    private authService: AuthService,
    private toastService: ToastService,
    private confirmationModal: ConfirmationModalService,
  ) {}

  ngOnInit(): void {
    this.authService.getUser().subscribe({
      next: (user) => {
        this.currentUser = user;
        this.isAdmin = user?.roles?.includes('admin') ?? false;
        this.applyReadonlyState();
      },
      error: () => {
        this.isAdmin = false;
        this.applyReadonlyState();
      },
    });

    this.applyReadonlyState();
  }

  protected get isSubmitted(): boolean {
    return !!this.event?.rules_conduct_submitted_at;
  }

  protected get submissionDate(): string | null {
    return this.event?.rules_conduct_submitted_at ?? null;
  }

  protected get allChecked(): boolean {
    return this.acknowledgements.controls.every((c) => c.value === true);
  }

  protected get canSubmit(): boolean {
    return this.isAdmin && !this.submissionLocked && !this.isSubmitted && this.allChecked && !this.submitting;
  }

  protected submit(): void {
    if (!this.canSubmit || !this.event?.id) {
      return;
    }

    const values = this.acknowledgements.controls.map((c) => c.value);
    this.submitting = true;

    this.rulesConductService.submit(this.event.id, values).subscribe({
      next: (response) => {
        this.submitting = false;
        if (response?.data) {
          this.event = response.data;
          this.eventUpdated.emit(this.event);
          this.applyReadonlyState();
        }
        this.toastService.success('Rules and Conduct agreement submitted successfully.');
      },
      error: (error) => {
        this.submitting = false;
        const message = error?.error?.message ?? 'Unable to submit Rules and Conduct agreement.';
        this.toastService.error(message);
      },
    });
  }

  protected async reset(): Promise<void> {
    if (!this.isAdmin || !this.isSubmitted || !this.event?.id) {
      return;
    }

    const confirmed = await this.confirmationModal.confirm({
      title: 'Reset Rules and Conduct submission?',
      message: 'The hirer will need to review and submit the Rules and Conduct form again. This action cannot be undone.',
      confirmLabel: 'Reset submission',
      cancelLabel: 'Cancel',
      confirmButtonStyle: 'danger',
    });

    if (!confirmed) {
      return;
    }

    this.resetting = true;
    this.rulesConductService.reset(this.event.id).subscribe({
      next: (response) => {
        this.resetting = false;
        if (response?.data) {
          this.event = response.data;
          this.eventUpdated.emit(this.event);
          this.applyReadonlyState();
        }
        this.toastService.success('Rules and Conduct submission reset.');
      },
      error: (error) => {
        this.resetting = false;
        const message = error?.error?.message ?? 'Unable to reset Rules and Conduct submission.';
        this.toastService.error(message);
      },
    });
  }

  private applyReadonlyState(): void {
    if (this.isSubmitted) {
      this.acknowledgements.controls.forEach((control) => {
        control.setValue(true, { emitEvent: false });
        control.disable({ emitEvent: false });
      });
    } else if (!this.isAdmin || this.submissionLocked) {
      this.acknowledgements.controls.forEach((control) => {
        control.disable({ emitEvent: false });
      });
    } else {
      this.acknowledgements.controls.forEach((control) => {
        control.enable({ emitEvent: false });
      });
    }
  }
}
